import React, { useState, useMemo, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';

const ApprovalGroupsTable = ({ data = [], totalRecords, isLoading, onPage, lazyParams, onEdit, onHistory, resetPagination = false }) => {
    const { th } = useLanguage();
    const safeParams = lazyParams || { page: 1, rows: 10 };

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [currentFirst, setCurrentFirst] = useState(0);

    useEffect(() => {
        if (resetPagination) {
            setCurrentFirst(0);
            setSortField(null);
            setSortOrder(null);
        }
    }, [resetPagination]);

    const handlePage = (event) => {
        setCurrentFirst(event.first);
        onPage(event);
    };

    const onSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        if (!sortField) return data;

        return [...data].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortOrder === 1 ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            if (aVal < bVal) return sortOrder === 1 ? -1 : 1;
            if (aVal > bVal) return sortOrder === 1 ? 1 : -1;
            return 0;
        });
    }, [data, sortField, sortOrder]);

    const actionTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')}></i>
                <i onClick={() => onHistory(rowData)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    return (
        <DataTable
            value={sortedData}
            paginator
            lazy
            first={currentFirst}
            rows={safeParams.rows}
            totalRecords={totalRecords}
            loading={isLoading}
            onPage={handlePage}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            dataKey="id"
            tableStyle={{ minWidth: '50rem' }}
            showGridlines
            stripedRows
            emptyMessage={th('approvalGroups.emptyMessage')}
            removableSort
            sortMode="single"
        >
            <Column body={actionTemplate} style={{ width: '5%' }} align="center" />
            <Column field="groupName" header={th('approvalGroups.groupName')} sortable style={{ minWidth: '350px' }} />
            <Column field="usersName" header={th('common.members')} sortable style={{ minWidth: '250px' }} />
            <Column field="createdName" header={th('common.created')} sortable style={{ minWidth: '200px' }} />
            <Column field="createdDate" header={th('common.createdDate')} body={(row) => formatDateOnly(row?.createdDate)} sortable style={{ width: '200px' }} />
            <Column field="modifiedName" header={th('common.updatedBy')} sortable style={{ minWidth: '200px' }} />
            <Column field="modifiedDate" header={th('common.updatedDate')} body={(row) => formatDateOnly(row?.modifiedDate)} sortable style={{ width: '200px' }} />
        </DataTable>
    );
};

export default ApprovalGroupsTable;
